import {
  Celebration,
  Email as MailIcon,
  Event as CalendarIcon,
  Group as UsersIcon,
  Language as GlobeIcon,
  LocationOn as MapPinIcon,
  NightShelter,
  Phone as PhoneIcon,
  Work as BriefcaseIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import strings from 'common/Translation/Translate';
import DrawerMenu from 'components/DrawerMenu';
import React, { FC } from 'react';
import { formatCurrency } from 'utils/validators/HelperFunctions';
import PageLoader from '../../../../components/PageLoader';

interface Props {
  data: any;
  handleClose: any;
  openPdfDrawer: string | boolean;
}

const ViewCompanyProfile: FC<Props> = ({ data, handleClose, openPdfDrawer }) => {
  return (
    <DrawerMenu
      width="80%"
      open={openPdfDrawer !== false}
      handleClose={handleClose}
      closeButton={true}
      title={strings.company_profile}
      dropBack={true}>
      <Box width="100%" sx={{ py: 2, px: 0 }}>
        <ViewCompanyProfile2 companyMaster={data} isLoading={false} />
      </Box>
    </DrawerMenu>
  );
};

export default ViewCompanyProfile;

interface CompanyMaster {
  accountType: { label: string };
  branches: Branch[];
  companyCategory: { label: string };
  companyName: string;
  hqAddress: string;
  hqCity: { label: string };
  hqCountry: { label: string };
  hqEmail: string;
  hqPhoneNo: { value: string };
  hqWebsite: string;
  industryType: { label: string };
  corporateRates: CorporateRate[];
  primaryManagers: [{ label: string; value: string }];
}

interface Branch {
  id: string;
  branchAddress: string;
  branchEmail: string;
  branchName: string;
  branchPhoneNo: { countryCode: string; value: string };
  branchWebsite: string;
  city: { label: string };
  cityPotentials: CityPotential[];
  contacts: Contact[];

  country: { label: string };
  secondaryManagers: [{ label: string; value: string }];
}

interface Contact {
  id: string;
  designation: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: { countryCode: string; value: string };
  title: { label: string };
  type: 'PRIMARY' | 'SECONDARY';
}

interface CityPotential {
  id: string;
  city: { label: string };
  competitions: Competition[];
  eventCount: number;
  eventRevenue: number;
  fitRoomNights: number;
  fitRoomPotential: number;
  miceEventCount: number;
  miceEventPotential: number;
  miceEvents: MiceEvent[];
  region: string;
  roomNights: number;
  roomRevenue: number;
}

interface Competition {
  id: string;
  fitRoomNights: number;
  hotelName: string;
  miceNights: number;
  reason: string;
}

interface MiceEvent {
  id: string;
  eventMonths: [{ label: string; value: string }];
  eventType: { label: string; value: string };
  eventSubtype: string;
  noOfPax: number;
  potentialRevenue: number;
  reminderBeforeDays: number;
  roomNights: number;
}

interface CorporateRate {
  id: string;
  validFrom: string;
  validTo: string;
  issuedBy: { label: string };
  eventRate: number;
  corporateRateInclusion?: string;
  roomRateMappings: RoomRateMapping[];
  timeSlotRateMappings: TimeSlotRateMapping[];
}

interface RoomRateMapping {
  id: string;
  roomTypeId: string;
  roomTypeName: string;
  rate: number;
}

interface TimeSlotRateMapping {
  id: string;
  partOfDayName?: string;
  partOfDay?: string;
  rate: number;
}

interface Props2 {
  companyMaster: CompanyMaster;
  isLoading: boolean;
}

const DetailItem: React.FC<{
  label: string;
  value: string | number;
  icon?: React.ReactNode;
}> = ({ label, value, icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
    {icon && <Box sx={{ color: 'primary.main' }}>{icon}</Box>}
    <Box>
      <Typography variant="subtitle2" color="text.secondary">
        <b>{label}</b>: {value}
      </Typography>
    </Box>
  </Box>
);

const ViewCompanyProfile2 = ({ companyMaster, isLoading }: Props2) => {
  const getCategoryColor = (category: string) => {
    if (category.startsWith('A')) {
      return 'success';
    } else if (category.startsWith('B')) {
      return 'info';
    } else if (category.startsWith('C')) {
      return 'warning';
    } else {
      return 'default';
    }
  };

  return (
    <>
      {isLoading && <PageLoader />}
      <Box sx={{ maxWidth: '1200px', mx: 'auto', px: 3, pt: 1, pb: 3 }}>
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
          }}>
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {companyMaster.companyName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {companyMaster.industryType.label}
            </Typography>
          </Box>
          <Chip
            label={companyMaster.companyCategory.label}
            color={getCategoryColor(companyMaster.companyCategory.label)}
            size="small"
          />
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Company Information
                  </Typography>
                </Box>
                <DetailItem
                  label="Industry Type"
                  value={companyMaster.industryType.label}
                  icon={<BriefcaseIcon sx={{ fontSize: 18 }} />}
                />
                <DetailItem
                  label="Account Type"
                  value={companyMaster.accountType.label}
                  icon={<UsersIcon sx={{ fontSize: 18 }} />}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {companyMaster.primaryManagers.map((i: any) => (
                    <Chip key={i.value} label={i.label} size="small" />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PhoneIcon color="primary" sx={{ fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Contact Information
                  </Typography>
                </Box>
                <DetailItem
                  label="Phone Number"
                  value={companyMaster.hqPhoneNo.value}
                  icon={<PhoneIcon sx={{ fontSize: 18 }} />}
                />
                <DetailItem label="Email" value={companyMaster.hqEmail} icon={<MailIcon sx={{ fontSize: 18 }} />} />
                <DetailItem
                  label="Website"
                  value={companyMaster.hqWebsite || 'Not provided'}
                  icon={<GlobeIcon sx={{ fontSize: 18 }} />}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <MapPinIcon color="primary" sx={{ fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Location Information
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <DetailItem
                      label="HQ Address"
                      value={companyMaster.hqAddress}
                      icon={<MapPinIcon sx={{ fontSize: 18 }} />}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DetailItem
                      label="Country"
                      value={companyMaster.hqCountry.label}
                      icon={<GlobeIcon sx={{ fontSize: 18 }} />}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DetailItem
                      label="City"
                      value={companyMaster.hqCity.label}
                      icon={<MapPinIcon sx={{ fontSize: 18 }} />}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {companyMaster.branches.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ mt: 1 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Branches
                </Typography>
                <Stack spacing={2}>
                  {companyMaster.branches.map((branch: Branch) => (
                    <Accordion
                      key={branch.id}
                      sx={{ border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {branch.branchName}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                Secondary Managers
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {branch.secondaryManagers.map((manager: any) => (
                                  <Chip key={manager.value} label={manager.label} size="small" />
                                ))}
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <DetailItem
                              label="Address"
                              value={branch.branchAddress}
                              icon={<MapPinIcon sx={{ fontSize: 18 }} />}
                            />
                            <DetailItem
                              label="Location"
                              value={`${branch.city.label}, ${branch.country.label}`}
                              icon={<GlobeIcon sx={{ fontSize: 18 }} />}
                            />
                          </Grid>
                        </Grid>

                        {/*---Contacts---*/}
                        {branch.contacts.length > 0 && (
                          <>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                              Contacts
                            </Typography>
                            <Grid container spacing={1.5}>
                              {branch.contacts.map((contact: Contact) => (
                                <Grid item xs={12} sm={6} key={contact.id} mb={1}>
                                  <Paper
                                    elevation={0}
                                    sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                      <Chip
                                        label={contact.type}
                                        color={contact.type === 'PRIMARY' ? 'primary' : 'default'}
                                        size="small"
                                      />
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {contact.title.label} {contact.firstName} {contact.lastName}
                                      </Typography>
                                    </Box>
                                    <DetailItem
                                      label="Designation"
                                      value={contact.designation}
                                      icon={<BriefcaseIcon sx={{ fontSize: 18 }} />}
                                    />
                                    <DetailItem
                                      label="Email"
                                      value={contact.email}
                                      icon={<MailIcon sx={{ fontSize: 18 }} />}
                                    />
                                    <DetailItem
                                      label="Phone"
                                      value={contact.phone.value}
                                      icon={<PhoneIcon sx={{ fontSize: 18 }} />}
                                    />
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </>
                        )}

                        {/*---City Potentials---*/}
                        {branch.cityPotentials.length > 0 && (
                          <Grid container spacing={1.5}>
                            {branch.cityPotentials.map(cityPotential => (
                              <Grid item xs={12} key={cityPotential.id}>
                                <Paper
                                  elevation={0}
                                  sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                  <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                      {cityPotential.city.label} {cityPotential.region && `(${cityPotential.region})`}
                                    </Typography>
                                  </Box>

                                  {/* Summary Metrics */}
                                  <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={6} sm={4}>
                                      <DetailItem label="Room Nights" value={cityPotential?.roomNights?.toString()} />
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                      <DetailItem
                                        label="Room Revenue"
                                        value={formatCurrency(cityPotential.roomRevenue)}
                                      />
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                      <DetailItem label="Event Count" value={cityPotential?.eventCount?.toString()} />
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                      <DetailItem
                                        label="Event Revenue"
                                        value={formatCurrency(cityPotential.eventRevenue)}
                                      />
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                      <DetailItem
                                        label="FIT Room Nights"
                                        value={cityPotential?.fitRoomNights?.toString()}
                                      />
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                      <DetailItem
                                        label="FIT Potential"
                                        value={formatCurrency(cityPotential.fitRoomPotential)}
                                      />
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                      <DetailItem
                                        label="MICE Events"
                                        value={cityPotential?.miceEventCount?.toString()}
                                      />
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                      <DetailItem
                                        label="MICE Potential"
                                        value={formatCurrency(cityPotential.miceEventPotential)}
                                      />
                                    </Grid>
                                  </Grid>

                                  {/* MICE Events */}
                                  {cityPotential.miceEvents.length > 0 && (
                                    <>
                                      <Divider sx={{ my: 2 }} />
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                        MICE Events
                                      </Typography>
                                      <Stack spacing={1.5}>
                                        {cityPotential.miceEvents.map(event => (
                                          <Paper
                                            key={event.id}
                                            elevation={0}
                                            sx={{
                                              p: 1.5,
                                              border: '1px solid',
                                              borderColor: 'divider',
                                              borderRadius: 2,
                                            }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                              {event.eventType.label}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              Months: {event.eventMonths.map(m => m.label).join(', ')}
                                            </Typography>
                                            <Grid container spacing={1} sx={{ mt: 1 }}>
                                              <Grid item xs={6} sm={3}>
                                                <DetailItem label="No. of Pax" value={event.noOfPax.toString()} />
                                              </Grid>
                                              <Grid item xs={6} sm={3}>
                                                <DetailItem label="Room Nights" value={event.roomNights.toString()} />
                                              </Grid>
                                              <Grid item xs={6} sm={3}>
                                                <DetailItem
                                                  label="Revenue"
                                                  value={formatCurrency(event.potentialRevenue)}
                                                />
                                              </Grid>
                                              <Grid item xs={6} sm={3}>
                                                <DetailItem
                                                  label="Reminder (days before)"
                                                  value={event.reminderBeforeDays.toString()}
                                                />
                                              </Grid>
                                            </Grid>
                                          </Paper>
                                        ))}
                                      </Stack>
                                    </>
                                  )}

                                  {/* Competitions */}
                                  {cityPotential.competitions.length > 0 && (
                                    <>
                                      <Divider sx={{ my: 2 }} />
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Competitions
                                      </Typography>
                                      <Grid container spacing={1.5}>
                                        {cityPotential.competitions.map(competition => (
                                          <Grid item xs={12} sm={6} key={competition.id}>
                                            <Paper
                                              elevation={0}
                                              sx={{
                                                p: 1.5,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 2,
                                              }}>
                                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                                {competition.hotelName}
                                              </Typography>
                                              {competition.reason && (
                                                <Typography variant="caption" color="text.secondary">
                                                  Reason: {competition.reason}
                                                </Typography>
                                              )}
                                              <Grid container spacing={1} sx={{ mt: 1 }}>
                                                <Grid item xs={6}>
                                                  <DetailItem
                                                    label="FIT Nights"
                                                    value={competition.fitRoomNights.toString()}
                                                    icon={<NightShelter sx={{ fontSize: 18 }} />}
                                                  />
                                                </Grid>
                                                <Grid item xs={6}>
                                                  <DetailItem
                                                    label="MICE Nights"
                                                    value={competition.miceNights?.toString()}
                                                    icon={<Celebration sx={{ fontSize: 18 }} />}
                                                  />
                                                </Grid>
                                              </Grid>
                                            </Paper>
                                          </Grid>
                                        ))}
                                      </Grid>
                                    </>
                                  )}
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        )}

                        {/*---Corporate Rates---*/}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                  {companyMaster?.corporateRates?.length > 0 && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0 }}>
                        Corporate Rates
                      </Typography>
                      <Grid container>
                        {companyMaster?.corporateRates.map(rate => (
                          <Grid item xs={12} key={rate.id}>
                            <Paper
                              elevation={0}
                              sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                              <Box sx={{ mb: 2 }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 0.5,
                                  }}>
                                  <Chip
                                    icon={<CalendarIcon sx={{ fontSize: 16 }} />}
                                    label={`${new Date(rate.validFrom).toLocaleDateString()} - ${new Date(
                                      rate.validTo,
                                    ).toLocaleDateString()}`}
                                    size="small"
                                  />
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  Issued by: {rate.issuedBy?.label || 'N/A'}
                                </Typography>
                              </Box>

                              <Grid container spacing={2}>
                                {/* Inclusions */}
                                <Grid item xs={12} md={4}>
                                  {rate.eventRate !== undefined && (
                                    <Box sx={{ mb: 1.5 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                                        Event Rate
                                      </Typography>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {formatCurrency(rate.eventRate)}
                                      </Typography>
                                    </Box>
                                  )}
                                  {rate.corporateRateInclusion && (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                                        Inclusions
                                      </Typography>
                                      <Typography variant="body2">{rate.corporateRateInclusion}</Typography>
                                    </Box>
                                  )}
                                </Grid>

                                {/* Room Rates */}
                                <Grid item xs={12} md={4}>
                                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
                                    Room Rates
                                  </Typography>
                                  {rate.roomRateMappings.length > 0 ? (
                                    <Stack spacing={0.5}>
                                      {rate.roomRateMappings.map(roomRate => (
                                        <Box
                                          key={roomRate.id}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            p: 0.75,
                                            bgcolor: 'background.default',
                                            borderRadius: 0.75,
                                          }}>
                                          <Typography variant="caption">{roomRate.roomTypeName}</Typography>
                                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                            {formatCurrency(roomRate.rate)}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Stack>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No room rates available
                                    </Typography>
                                  )}
                                </Grid>

                                {/* Time Slot Rates */}
                                <Grid item xs={12} md={4}>
                                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
                                    Time Slot Rates
                                  </Typography>
                                  {rate.timeSlotRateMappings.length > 0 ? (
                                    <Stack spacing={0.5}>
                                      {rate.timeSlotRateMappings.map(timeSlot => (
                                        <Box
                                          key={timeSlot.id}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            p: 0.75,
                                            bgcolor: 'background.default',
                                            borderRadius: 0.75,
                                          }}>
                                          <Typography variant="caption">{timeSlot.partOfDayName ?? timeSlot.partOfDay}</Typography>
                                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                            {formatCurrency(timeSlot.rate)}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Stack>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No time slot rates available
                                    </Typography>
                                  )}
                                </Grid>
                              </Grid>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                </Stack>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
};

export { ViewCompanyProfile2 };
